/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Modal, Fade, Backdrop, TextField } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import Autocomplete from '@material-ui/lab/Autocomplete';
import PropTypes from 'prop-types';

import { getPatients } from '../store/actions/patients';
import { getAdmin, editGroup } from '../store/actions/admin';

const useStyles = makeStyles((theme) => ({
  title: {
    fontSize: 18,
    margin: 0,
    color: '#1e3f9e',
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    padding: '2rem',
    paddingTop: '1rem',
    minWidth: 550,
    maxWidth: '700px',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    boxShadow: theme.shadows[5],
    outline: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  buttonRow: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: '2rem',
    width: '100%',
    justifyContent: 'space-around',
  },
  button: {
    width: '30%',
    padding: '0.6rem',
    color: 'white',
    backgroundColor: '#00d1b2',
    '&:hover': {
      backgroundColor: '#00bca0',
    },
  },
  form: {
    margin: '1rem',
    width: '100%',
    display: 'flex',
    flexFlow: 'row wrap',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    width: '50%',
    alignItems: 'center',
  },
  field: {
    width: '100%',
  },
  nameFieldContainer: {
    width: '80%',
    marginLeft: '5%',
    marginRight: '5%',
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
    flexFlow: 'column',
  },
}));

const EditGroupModal = ({ open, setOpen, group }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const patients = useSelector((state) => state.patients.patientsList);
  const users = useSelector((state) => state.admin.usersList);
  const { id } = useParams();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [name, setName] = useState(group.name);

  useEffect(() => {
    dispatch(getPatients());
    dispatch(getAdmin());
    if (group.members) setSelectedUsers(group.members);
    if (group.patients) setSelectedPatients(group.patients);
  }, []);

  const handleClose = () => {
    if (group.members) setSelectedUsers(group.members);
    else setSelectedUsers([]);
    if (group.patients) setSelectedPatients(group.patients);
    else setSelectedPatients([]);
    setName(group.name);
    setOpen(false);
  };

  const getLabel = (value) => {
    return `${value.first_name} ${value.last_name}`;
  };

  const submitHandler = () => {
    const body = {
      name,
      members: selectedUsers.map((member) => member.id),
      patients: selectedPatients.map((patient) => patient.id),
    };
    dispatch(editGroup(body, id));
    setOpen(false);
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={classes.modal}
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
    >
      <Fade in={open}>
        <div className={classes.paper}>
          <h2 className={classes.title}>Edit Group</h2>
          <form className={classes.form} onSubmit={submitHandler}>
            <div className={classes.nameFieldContainer}>
              <TextField
                className={classes.field}
                placeholder="*Group Name"
                variant="outlined"
                name="name"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className={classes.column}>
              <div className={classes.nameFieldContainer}>
                {users && (
                  <Autocomplete
                    multiple
                    options={users}
                    className={classes.field}
                    getOptionLabel={(option) => getLabel(option)}
                    id="auto-complete"
                    getOptionSelected={(option, value) => option.id === value.id}
                    autoComplete
                    defaultValue={selectedUsers}
                    onChange={(event, newValue) => setSelectedUsers(newValue)}
                    renderInput={(params) => <TextField {...params} label="Members" margin="normal" />}
                  />
                )}
              </div>
            </div>
            <div className={classes.column}>
              <div className={classes.nameFieldContainer}>
                {patients && (
                  <Autocomplete
                    multiple
                    options={patients}
                    className={classes.field}
                    getOptionLabel={(option) => getLabel(option)}
                    id="auto-complete"
                    getOptionSelected={(option, value) => option.id === value.id}
                    autoComplete
                    defaultValue={selectedPatients}
                    onChange={(event, newValue) => setSelectedPatients(newValue)}
                    renderInput={(params) => <TextField {...params} label="Patients" margin="normal" />}
                  />
                )}
              </div>
            </div>
            <div className={classes.buttonRow}>
              <Button className={classes.button} size="small" variant="contained" type="submit" disabled={!name.length}>
                Save Changes
              </Button>
              <Button className={classes.button} size="small" variant="contained" onClick={handleClose}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </Fade>
    </Modal>
  );
};

EditGroupModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  group: PropTypes.any.isRequired,
};

export default EditGroupModal;
